import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  orderStatusFormatted,
  orderTrackingType,
  orderTrackingTypeTranslate,
  PAYMENT_METHOD,
  productGroupType,
  UNIT_LIST,
  UNIT_LIST_CONVERT,
  WAREHOUSE_TRANSFER_CREATE_TYPE,
} from "src/constant";
import useAuthGlobal from "src/context/useAuthGlobal";
import usePaymentMethodList from "src/hooks/payment-method/usePaymentMethodList";
import useWarehouseTransferCreate from "src/hooks/warehouse-transfer/useWarehouseTransferCreate";
import useWarehouseTransferStatus from "src/hooks/warehouse-transfer/useWarehouseTransferStatus";
import useWareHouseList from "src/hooks/warehouse/useWareHouseList";
import useAppGlobal from "src/context/useAppGlobal";
import useWarehouseTransferDetail from "src/hooks/warehouse-transfer/useWarehouseTransferDetail";
import ImportReviewDialog from "../inventory-in/ImportReviewDialog";
import useSettingBatchCode from "src/hooks/setting/batch-code/useSettingBatchCode";
import { CheckCircle, Close } from "@mui/icons-material";
import useWTCreateExport from "src/hooks/warehouse-transfer/useWTCreateExport";
import useWarehouseTransferRemove from "src/hooks/warehouse-transfer/useWarehouseTransferRemove";
import useInventoryBatchList from "src/hooks/inventory/useInventoryBatchList";
import useDebounce from "src/hooks/useDebounce";
import useWTCodeScanExport from "src/hooks/warehouse-transfer/useWTCodeScanExport";
import useWTProdRemove from "src/hooks/warehouse-transfer/useWTProdRemove";
import useWTUpdateProdExport from "src/hooks/warehouse-transfer/useWTUpdateProdExport";
import useProductDetail from "src/hooks/product/useProductDetail";
import { unitConvert } from "src/helper/unitConvert";
import useWTUpdate from "src/hooks/warehouse-transfer/useWTUpdate";
import ExportProductsTable from "./ExportProductsTable";
import MallScannerMUI from "src/components/shared/ScanningInput";
import useInventoryList from "src/hooks/inventory/useInventoryList";
import UploadCloud from "src/components/shared/icon/UploadCloud";
import useWarehouseTransferImportSeries from "src/hooks/warehouse-transfer/useWarehouseTransferImportSeries";
import useWarehouseTransferImportSeriesUpdate from "src/hooks/warehouse-transfer/useWarehouseTransferImportSeriesUpdate";
import useInventoryProductDetail from "src/hooks/inventory/useInventoryProductDetail";
import { PERMISSIONS, useRoleCheck } from "src/hooks/permission/permission";
import SearchList from "src/components/shared/SearchList";

const requiredFilter = {
  limit: 100,
  skip: 0,
  sortField: "createdAt",
  sortOrder: "desc",
  searchField: "name",
};

export default function TempExport({ item, updateItem, title }) {
  const { closeDialog } = useAppGlobal();
  const { userMe } = useAuthGlobal();
  const permissionCheck = useRoleCheck();
  const [prodPick, setProdPick] = useState();
  const [prodList, setProdList] = useState([]);
  const [stableFields, setStableFields] = useState({
    type: orderTrackingType.new,
    fromWarehouse: userMe?.currentWarehouse,
  });

  const [isScanned, setIsScanned] = useState(false);
  const [isPrint, setIsPrint] = useState(null);
  const [wtTransferId, setWtTransferId] = useState();
  const [isOver, setIsOver] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState({});
  const inputRef = useRef(null);
  const debounceCode = useDebounce(prodPick?.itemCodes, 200);
  const { data: warehouse } = useWareHouseList({ ...requiredFilter, ...selectedWarehouse });

  const { data: batch } = useInventoryBatchList({
    warehouse: stableFields?.fromWarehouse?._id ? stableFields?.fromWarehouse?._id : (stableFields?.fromWarehouse?._id ?? userMe?.currentWarehouse?._id),
    product: prodPick?.productId?._id,
    productModel: prodPick?.productModelId?._id ? prodPick?.productModelId?._id : prodPick?.productModelId,
    limit: 200,
    skip: 0,
  });
  const { data: paymentMethods } = usePaymentMethodList({ type: PAYMENT_METHOD.EXPORT });
  const { data: detail, isValidating: detailLoading, mutate: detailMutate } = useWarehouseTransferDetail(updateItem?._id ?? wtTransferId);

  useEffect(() => {
    if (detail?.data) {
      localStorage.setItem("wtTransferId", detail?.data?._id);
    }
  }, [detail?.data?._id, detailLoading]);

  const { trigger: createExport } = useWarehouseTransferCreate();
  const { trigger: removeWT } = useWarehouseTransferRemove();
  const { trigger: createProd } = useWTCreateExport(wtTransferId ?? updateItem?._id);
  const { data: batchCode } = useSettingBatchCode();
  const { data: product } = useInventoryList({
    productGroupType: stableFields?.type === orderTrackingType.export ? productGroupType.manufacture : productGroupType.retail,
    warehouse: stableFields?.fromWarehouse?._id,
  });
  const { trigger: scanCode } = useWTCodeScanExport(wtTransferId ?? updateItem?._id);
  const { trigger: importSeries } = useWarehouseTransferImportSeries(wtTransferId ?? updateItem?._id);
  const { trigger: importSeriesUpdate } = useWarehouseTransferImportSeriesUpdate(wtTransferId ?? updateItem?._id, prodPick?.transferDetailId);
  const { trigger: exportStatus } = useWarehouseTransferStatus(wtTransferId ?? updateItem?._id);
  const { trigger: removeProd } = useWTProdRemove(updateItem?._id ?? wtTransferId);
  const { trigger: updateProd } = useWTUpdateProdExport(updateItem?._id ?? wtTransferId);
  const { data: prodDetail } = useProductDetail(prodPick?.productId?._id);
  const { trigger: updateWT } = useWTUpdate();
  const { data: productDetail } = useInventoryProductDetail(prodPick?.productId?._id);

  const totalQtySlip = detail?.data?.totalQty ?? 0;
  const totalExported = detail?.data?.totalExported ?? 0;
  const isFulled = totalExported > 0 && totalQtySlip > 0 && totalQtySlip === totalExported;

  useEffect(() => {
    if (prodDetail?.data?.unit) {
      setProdPick((prev) => ({ ...prev, unit: prodDetail?.data?.unit }));
    }
  }, [prodDetail?.data?.unit, prodPick?.productId?._id]);

  const handleChangeValue = ({ name, value }) => {
    setProdPick((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeStableValue = ({ name, value }) => {
    setStableFields((prev) => ({ ...prev, [name]: value }));
  };

  const getDefaultToWarehouseId = (warehouseList, fromWarehouseId, userWarehouseId) => {
    if (!Array.isArray(warehouseList)) return undefined;
    return warehouseList.find((v) => v?._id && v._id !== fromWarehouseId && v._id !== userWarehouseId)?._id;
  };

  // useEffect(() => {
  //   if (stableFields?.type !== orderTrackingType.new || updateItem) return;
  //   if (!Array.isArray(warehouse?.data) || !warehouse.data.length) return;

  //   const fromWarehouseId = stableFields?.fromWarehouse?._id ? stableFields?.fromWarehouse?._id : (stableFields?.fromWarehouse?._id ?? userMe?.currentWarehouse?._id);
  //   const currentToWarehouse = stableFields?.toWarehouse;
  //   const isCurrentValid =
  //     currentToWarehouse?._id && warehouse.data.some((v) => v._id === currentToWarehouse?._id && v._id !== fromWarehouseId && v._id !== userMe?.currentWarehouse?._id);

  //   if (isCurrentValid) return;

  //   const defaultToWarehouseId = getDefaultToWarehouseId(warehouse.data, fromWarehouseId, userMe?.currentWarehouse?._id);
  //   if (defaultToWarehouseId) {
  //     setStableFields((prev) => ({ ...prev, toWarehouse: defaultToWarehouseId }));
  //   }
  // }, [warehouse?.data, stableFields?.type, stableFields?.fromWarehouse?._id, stableFields?.toWarehouse?._id, updateItem, userMe?.currentWarehouse?._id]);

  const confirmRemove = (id, transferDetailId) => {
    enqueueSnackbar("Bạn có chắc chắn muốn xoá sản phẩm này?", {
      variant: "warning",
      persist: true,
      action: (snackbarId) => (
        <Stack
          direction="row"
          spacing={1}
        >
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              closeSnackbar(snackbarId);
              handleRemove(id, transferDetailId);
            }}
          >
            Xác nhận
          </Button>

          <Button
            size="small"
            variant="outlined"
            onClick={() => closeSnackbar(snackbarId)}
          >
            Huỷ
          </Button>
        </Stack>
      ),
    });
  };

  const handleRemove = async (id, transferDetailId) => {
    const res = await removeProd({
      pathIncluded: {
        transferDetailId: transferDetailId,
      },
    });
    if (res?.success) {
      setProdList(prodList?.filter((v) => v?.transferDetailId !== transferDetailId));
      setProdPick();
      return enqueueSnackbar("Xoá sản phẩm thành công", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const mapImportDetailsToErrorObj = (details = []) => {
    if (!Array.isArray(details)) return {};
    return details.reduce((acc, item) => {
      const key = item?.error;
      if (!key) return acc;
      const value = item?.value || "";
      const rowLabel = Number.isFinite(item?.row) ? ` (dòng ${item?.row})` : "";
      if (!acc[key]) acc[key] = [];
      acc[key].push(`${value}${rowLabel}`);
      return acc;
    }, {});
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Allow selecting the same file again after an import attempt.
    event.target.value = "";

    try {
      const isUpdate = Boolean(prodPick?.transferDetailId);
      const formData = new FormData();
      const selectedBatch = batch?.data?.find((item) => item?.batch?._id === prodPick?.batch)?.batch;
      const selectedProductModel = productDetail?.data?.productModels?.find((item) => item?._id === prodPick?.productModelId);

      const qty = ["piece", "bottle", "combo"]?.includes(prodDetail?.data?.unit)
        ? Number(prodPick?.qtyCal)
        : unitConvert({
            val: Number(prodPick?.qtyCal),
            from: prodPick?.unit,
            to: prodDetail?.data?.unit,
          });

      // FormData values are sent as strings, except for the uploaded File.
      // Skip optional values that are not available rather than sending "undefined".
      const appendIfDefined = (key, value) => {
        if (value !== undefined && value !== null && value !== "") {
          formData.append(key, String(value));
        }
      };

      if (isUpdate) {
        appendIfDefined("discount", prodPick?.discount);
        appendIfDefined("discount_price", prodPick?.discount_price);
        appendIfDefined("note", prodPick?.note);
        appendIfDefined("vat", prodPick?.vat);
        appendIfDefined("productModel", prodPick?.productModel ?? prodPick?.productModelId);
        appendIfDefined("batchCode", prodPick?.batchCode ?? selectedBatch?.code ?? prodPick?.batchName);
        appendIfDefined("lotNSX", prodPick?.lotNSX ?? selectedBatch?.lotNSX);
        appendIfDefined("mfg", prodPick?.mfg ?? selectedBatch?.mfg);
        appendIfDefined("exp", prodPick?.exp ?? selectedBatch?.exp);
        appendIfDefined("qty", Number.isFinite(qty) ? qty : prodPick?.qtyCal);
        formData.append("file", file);

        const res = await importSeriesUpdate({ body: formData });

        if (res?.success) {
          if (res?.data?.totalError > 0 || res?.data?.itemCodes?.totalError > 0) {
            const err = res?.data?.errorItemCodes || res?.data?.itemCodes?.errorItemCodes;
            handleConfirm(err);
          } else {
            setProdPick((prev) => ({ ...prev, itemCodes: undefined, files: [file] }));
            enqueueSnackbar("Import serial thành công", { variant: "success" });
          }
          return;
        }

        const errorObj = mapImportDetailsToErrorObj(res?.error?.details);
        if (Object.keys(errorObj).length > 0) {
          return handleConfirm(errorObj);
        }
        return enqueueSnackbar(res?.error?.message || "Import serial thất bại", { variant: "error" });
      }

      appendIfDefined("discount", prodPick?.discount ?? 0);
      appendIfDefined("discount_price", prodPick?.discount_price);
      appendIfDefined("note", prodPick?.note);
      // appendIfDefined("importedPrice", prodPick?.importedPrice ?? prodPick?.tempPrice ?? selectedProductModel?.price);
      appendIfDefined("qty", Number.isFinite(qty) ? qty : prodPick?.qtyCal);
      appendIfDefined("batch", prodPick?.batch);
      formData.append("file", file);

      const res = await importSeries({ body: formData });

      if (res?.success) {
        if (res?.data?.totalError > 0 || res?.data?.itemCodes?.totalError > 0) {
          const err = res?.data?.errorItemCodes || res?.data?.itemCodes?.errorItemCodes;
          handleConfirm(err);
        } else {
          setProdPick((prev) => ({ ...prev, itemCodes: undefined, files: [file] }));
          enqueueSnackbar("Import serial thành công", { variant: "success" });
        }
        return;
      }

      const errorObj = mapImportDetailsToErrorObj(res?.error?.details);
      if (Object.keys(errorObj).length > 0) {
        return handleConfirm(errorObj);
      }
      return enqueueSnackbar(res?.error?.message || "Import serial thất bại", { variant: "error" });
    } catch (error) {
      console.error("Import serial failed:", error);
      enqueueSnackbar("Import serial thất bại", { variant: "error" });
    }
  };

  const { total, totalQty } = useMemo(() => {
    const result = prodList?.reduce((acc, curr) => {
      const price = Number(curr?.tempPrice) || 0;
      const qty = Number(curr?.qtyCal) || 0;
      const discount = Number(curr?.discount) || 0;
      const total = price * qty * (1 - discount / 100);
      return acc + total;
    }, 0);

    const calQty = prodList?.reduce((acc, curr) => {
      return acc + Number(curr?.qtyCal);
    }, 0);

    const total = Math.floor(result / 1000) * 1000;
    const totalQty = calQty;
    return { total, totalQty };
  }, [prodList]);

  // UPDATE ITEM
  useEffect(() => {
    if (updateItem) {
      setStableFields({
        fromWarehouse: detail?.data?.fromWarehouse ?? updateItem?.fromWarehouse?._id,
        toWarehouse: detail?.data?.toWarehouse ?? updateItem?.toWarehouse?._id,
        paymentMethod: updateItem?.paymentMethod?._id,
        type: updateItem?.type === orderTrackingType.remove ? orderTrackingType.removed : updateItem?.type,
      });
      setProdList(
        detail?.data?.transferDetails?.map((v) => ({
          ...v,
          productId: v?.productModel?.product,
          productModelId: v?.productModel,
          batch: v?.batch,
          qtyCal: v?.qty,
          discount: v?.discount,
          // itemCodes: v?.itemCodes,
          batchName: v?.batch?.code,
          productName: v?.productModel?.product?.name,
          productMolName: v?.productModel?.product?.productGroup?.code ? v?.productModel?.product?.shortName : v?.productModel?.name,
          tempPrice: v?.price,
          note: v?.note,
          transferDetailId: v?._id,
        })),
      );
    }
  }, [updateItem, detailLoading, detail?.data]);

  useEffect(() => {
    if (!!item) {
      setProdPick({
        ...item,
        productId: item?.product,
        // productModelId: item?.productModels[0]?._id,
      });
      console.log("prod", prodPick);
    }
  }, [item]);

  // ---------- CREATE WAREHOUSE TRANSFER EXPORT
  const handleCreateExport = async () => {
    if (!updateItem?._id) {
      const res = await createExport({
        body: {
          statusOut: orderStatusFormatted.Draft,
          fromWarehouse: stableFields?.fromWarehouse?._id ? stableFields?.fromWarehouse?._id : stableFields?.fromWarehouse?._id,
          toWarehouse: stableFields?.toWarehouse?._id,
          paymentMethod: stableFields?.paymentMethod,
          type: stableFields?.type ?? orderStatusFormatted.New,
        },
      });
      if (res?.success) {
        setWtTransferId(res?.data?._id);
        localStorage.setItem("wtTransferId", res?.data?._id);
      } else {
        return enqueueSnackbar(res?.error?.message, { variant: "error" });
      }
    }
  };

  useEffect(() => {
    if (stableFields?.type === orderTrackingType.new) {
      if (stableFields?.paymentMethod && !prodPick?.transferDetailId) {
        handleCreateExport();
      }
    }
  }, [stableFields?.paymentMethod, stableFields?.type]);

  useEffect(() => {
    if (!!prodPick?.productId?._id && [orderTrackingType.remove, orderTrackingType.export]?.includes(stableFields?.type)) {
      handleCreateExport();
    }
  }, [prodPick?.productId?._id, stableFields?.type]);

  const addMoreProdModel = (id) => {
    const batchName = batch?.data?.find((v) => v?.batch?._id === prodPick?.batch) ?? "-";
    const prodName = product?.data?.find((v) => v?._id === prodPick?.productId?._id);
    const pModelName = productDetail?.data?.productModels?.find((v) => v?._id === prodPick?.productModelId);

    const item = {
      ...prodPick,
      transferDetailId: id,
      batchName: batchName,
      productName: `${prodName?.product?.name}`,
      productMolName: !prodName?.productGroup?.code ? pModelName?.name : prodName?.shortName,
      tempPrice: Number(pModelName?.price) ?? 0,
      totalPrice: Number(pModelName?.price) * Number(prodPick?.qtyCal) * (1 - Number(prodPick?.discount / 100)),
    };

    setProdList((prev) => [...prev, item]);
    setProdPick((prev) => ({ ...prev, transferDetailId: id }));
  };

  const handleUpdateProd = async () => {
    if (prodPick?.mode === "update") {
      const res = await updateProd({
        pathIncluded: {
          transferDetailId: prodPick?.transferDetailId,
        },
        body: {
          discount: Number(prodPick?.discount),
          note: prodPick?.note,
          qty: ["piece", "bottle", "combo"]?.includes(prodDetail?.data?.unit)
            ? Number(prodPick?.qtyCal)
            : unitConvert({
                val: Number(prodPick.qtyCal),
                from: prodPick?.unit,
                to: prodDetail?.data?.unit,
              }),
          batch: prodPick?.batch?._id ?? prodPick?.batch,
        },
      });
      if (res?.success) {
        await handleUpdateWT();
        const index = prodList?.findIndex((v) => v?.transferDetailId === res?.data?._id);
        const list = [...prodList];
        list[index] = { ...list[index], ...res?.data, qtyCal: res?.data?.qty };
        setProdList(list);
        await detailMutate();
        return enqueueSnackbar("Cập nhật sản phẩm thành công", { variant: "success" });
      } else {
        return enqueueSnackbar(res?.error?.message, { variant: "error" });
      }
    }
  };

  const handleCreateProd = async (mode) => {
    if (prodPick?.mode !== "update") {
      if (prodPick?.transferDetailId) {
        setProdPick(undefined);
      } else {
        const res = await createProd({
          body: {
            discount: Number(prodPick?.discount),
            note: prodPick?.note,
            qty: ["piece", "bottle", "combo"]?.includes(prodDetail?.data?.unit)
              ? Number(prodPick?.qtyCal)
              : unitConvert({
                  val: Number(prodPick.qtyCal),
                  from: prodPick?.unit,
                  to: prodDetail?.data?.unit,
                }),
            batch: prodPick?.batch,
          },
        });
        if (res?.success) {
          addMoreProdModel(res?.data?._id);
          if (mode === WAREHOUSE_TRANSFER_CREATE_TYPE.CREATE) {
            setProdPick(undefined);
          }
          return enqueueSnackbar("Tạo sản phẩm thành công", { variant: "success" });
        } else {
          return enqueueSnackbar(res?.error?.message, { variant: "error" });
        }
      }
    }
  };

  const handleClose = () => {
    if (
      (!updateItem?._id && isPrint === null && stableFields?.paymentMethod && stableFields?.type === orderTrackingType.new) ||
      ([orderTrackingType.export, orderTrackingType.remove]?.includes(stableFields?.type) && wtTransferId)
    ) {
      enqueueSnackbar({
        variant: "warning",
        persist: true,
        content: (key) => (
          <Paper
            elevation={6}
            sx={{
              p: 2,
              backgroundColor: "var(--pending-txt)",
              color: "var(--black)",
              maxWidth: 360,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography fontWeight="bold">Bạn có muốn xoá thông tin xuất kho hiện tại không?</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  closeSnackbar(key);
                }}
              >
                Hủy
              </Button>
              <Button
                size="small"
                sx={{ background: "var(--black)" }}
                variant="contained"
                onClick={() => {
                  closeSnackbar(key);
                  handleCloseWTExport();
                }}
              >
                Đồng ý
              </Button>
            </Box>
          </Paper>
        ),
      });
    }
    if (!!isPrint || !!updateItem || (!stableFields?.paymentMethod && !wtTransferId)) {
      closeDialog();
    }
  };

  const handleCloseWTExport = async () => {
    const res = await removeWT({
      pathIncluded: {
        id: wtTransferId,
      },
    });
    if (res?.success) {
      enqueueSnackbar("Đã đóng phiếu xuất!", { variant: "success" });
      closeDialog();
      return localStorage.removeItem("wtTransferId");
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  const handleFilterErrorCode = (key) => {
    setProdPick((prev) => ({ ...prev, itemCodes: null }));
    closeSnackbar(key);
  };

  const handleConfirm = (errorObj) => {
    const action = (key) => (
      <Button
        onClick={() => handleFilterErrorCode(key)}
        sx={{ bgcolor: "var(--black)" }}
        variant="contained"
        size="small"
      >
        Xác nhận
      </Button>
    );
    return enqueueSnackbar(
      <div>
        <p style={{ margin: 0 }}>Vui lòng xác nhận xoá các mã lỗi:</p>
        {!!errorObj?.createFailed?.length && (
          <p style={{ margin: 0 }}>
            Mã lỗi: <strong>{String(errorObj?.createFailed)}</strong>
          </p>
        )}
        {!!errorObj?.duplicate?.length && (
          <p style={{ margin: 0 }}>
            Mã trùng: <strong>{String(errorObj?.duplicate)}</strong>
          </p>
        )}
        {!!errorObj?.existed?.length && (
          <p style={{ margin: 0 }}>
            Mã đã tồn tại: <strong>{String(errorObj?.existed)}</strong>
          </p>
        )}
        {!!errorObj?.notFound?.length && (
          <p style={{ margin: 0 }}>
            Mã không tìm thấy: <strong>{String(errorObj?.notFound)}</strong>
          </p>
        )}
        {!!errorObj?.notInBatch?.length && (
          <p style={{ margin: 0 }}>
            Mã không có trong lô: <strong>{String(errorObj?.notInBatch)}</strong>
          </p>
        )}
        {!!errorObj?.invalidPrefix?.length && (
          <p style={{ margin: 0 }}>
            Sai format: <strong>{String(errorObj?.invalidPrefix)}</strong>
          </p>
        )}
        {errorObj === "BatItemCodeExceed" && <p style={{ margin: 0, fontWeight: "bold" }}>Mã sản phẩm vượt quá số lượng cần nhập</p>}
      </div>,
      {
        variant: "warning",
        action,
        persist: true,
        preventDuplicate: true,
        anchorOrigin: { vertical: "top", horizontal: "center" },
      },
    );
  };

  const handleScanCode = async (item) => {
    const res = await scanCode({
      pathIncluded: {
        transferDetailId: prodPick?.transferDetailId,
      },
      body: {
        itemCodes: item,
      },
    });
    if (res?.success) {
      if (res?.data?.totalError > 0 || res?.data?.itemCodes?.totalError > 0) {
        const err = res?.data?.errorItemCodes || res?.data?.itemCodes?.errorItemCodes;
        handleConfirm(err);
      }
      if (res?.data?.totalError === 0 || res?.data?.itemCodes?.totalError === 0) {
        const index = prodList?.findIndex((v) => v?.batch === res?.data?.transferDetail?.batch);
        const prodArray = [...prodList];
        prodArray[index] = {
          ...prodArray[index],
          exportedQty: res?.data?.transferDetail?.exportedQty,
        };
        setProdList(prodArray);
        setProdPick((prev) => ({ ...prev, itemCodes: undefined }));
      }
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  useEffect(() => {
    if (!!debounceCode) {
      const refinedArray = debounceCode?.map((v) => v?.replaceAll("Shift", ""));
      if (prodDetail?.data?.productGroup?.skipValidItemCode) {
        handleScanCode(refinedArray);
      } else {
        const index = prodList?.findIndex((v) => v?.batch === prodPick?.batch);
        if (index > -1) {
          setProdPick((prev) => ({
            ...prev,
            transferDetailId: prodList[index]?.transferDetailId,
          }));
          if (!Array.isArray(batchCode?.data?.prefixItemCodes) || !Array.isArray(batchCode?.data?.stripItemCodes)) return;

          const validCodes = [];
          const invalidCodes = [];

          refinedArray.forEach((line) => {
            let cleaned = line;

            batchCode.data.stripItemCodes.forEach((strip) => {
              if (cleaned.startsWith(strip + "/")) {
                cleaned = cleaned.replace(strip + "/", "");
              } else if (cleaned.startsWith(strip)) {
                cleaned = cleaned.replace(strip, "");
              }
            });

            const hasValidPrefix = batchCode.data.prefixItemCodes.some((prefix) => cleaned.startsWith(prefix));

            if (hasValidPrefix) {
              validCodes.push(cleaned);
            } else {
              invalidCodes.push(cleaned);
            }
            if (invalidCodes?.length > 0) {
              setProdPick((prev) => ({ ...prev, itemCodes: undefined }));
              return enqueueSnackbar("Mã không hợp lệ", { variant: "warning" });
            }
          });
          handleScanCode(validCodes);
        }
      }
    }
  }, [debounceCode, batchCode?.data?.prefixItemCodes, batchCode?.data?.stripItemCodes, prodDetail?.data?.productGroup?.skipValidItemCode]);

  const totalTransferDetail = Array.isArray(detail?.data?.transferDetails) ? detail?.data?.transferDetails.find((v) => v?._id === prodPick?.transferDetailId)?.exportedQty : 0;

  const handleExport = async (statusOut) => {
    const res = await exportStatus({
      body: {
        warehouse: detail?.data?.toWarehouse?._id,
        statusOut: statusOut,
      },
    });
    if (res?.success) {
      setIsPrint(res?.data);
      return enqueueSnackbar("Thành công", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  const hasUnexportedQty = detail?.data?.transferDetails?.some((item) => {
    const exported = item.exportedQty ?? 0;
    return exported !== 0 && exported < item.qty;
  });

  const handleUpdateWT = async () => {
    const res = await updateWT({
      pathIncluded: {
        id: detail?.data?._id,
      },
      body: {
        ...stableFields,
        type: undefined,
        fromWarehouse: undefined,
        toWarehouse: stableFields?.toWarehouse?._id ?? stableFields?.toWarehouse,
      },
    });
    if (!res?.success) {
      return enqueueSnackbar("Cập nhập thông tin đơn hàng không thành công!", {
        variant: "error",
      });
    }
  };

  const handleConfirmUpdate = () => {
    setIsPrint(updateItem);
  };

  useEffect(() => {
    if (prodList?.length) {
      const item = prodList?.find((v) => v?.batch === prodPick?.batch);

      if ((item?.exportedQty ?? 0) > 0) {
        setIsScanned((prev) => !prev);
      }
    }
  }, [prodList?.length, prodPick?.batch]);

  useEffect(() => {
    if (prodPick?.batch && batch?.data) {
      const item = batch?.data?.find((v) => v?.batch?._id === prodPick?.batch);
      if (item) {
        setIsOver(Number(prodPick?.qtyCal) > item?.qty);
      }
    }
  }, [prodPick?.batch, batch?.data, prodPick?.qtyCal]);

  const isFileUploaded = Array.isArray(prodPick?.files) && prodPick.files.length > 0;

  useEffect(() => {
    if (Number(totalTransferDetail ?? 0) === Number(prodPick?.qtyCal ?? 0) && stableFields?.type !== orderTrackingType.export) {
      setProdPick();
    }
  }, [setProdPick, totalTransferDetail, prodPick?.qtyCal]);

  return (
    <>
      <DialogTitle
        fontWeight="bold"
        display="flex"
        justifyContent="space-between"
        sx={{ borderBottom: "1px solid var(--grey)", mb: "8px" }}
      >
        {!isPrint && <>{!!updateItem ? <>{updateItem?.type !== orderTrackingType.remove ? title : "Kiểm tra"}</> : "Tạo lệnh xuất kho"}</>}

        <IconButton
          onClick={handleClose}
          sx={{ width: "24px", height: "24px", ml: "auto" }}
        >
          <Close sx={{ fontSize: "22px" }} />
        </IconButton>
      </DialogTitle>
      {!isPrint ? (
        <DialogContent>
          <Grid
            container
            spacing={2}
          >
            {/* FIELDS */}
            <Grid
              item
              xs={5}
            >
              {/* TYPE */}
              <Box>
                <FormControl
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <RadioGroup
                    disabled={isScanned}
                    value={stableFields?.type}
                    onChange={(e) => handleChangeStableValue({ name: "type", value: e.target.value })}
                    aria-labelledby="demo-radio-buttons-group-label"
                    name="radio-buttons-group"
                    sx={{ display: "flex", flexDirection: "row" }}
                  >
                    <FormControlLabel
                      value={orderTrackingType.new}
                      control={<Radio />}
                      label={orderTrackingTypeTranslate.New}
                    />
                    <FormControlLabel
                      value={orderTrackingType.export}
                      control={<Radio />}
                      label={orderTrackingTypeTranslate.Export}
                    />

                    <FormControlLabel
                      value={orderTrackingType.remove}
                      control={<Radio />}
                      label={orderTrackingTypeTranslate.Removed}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
              {/* FROM */}
              <Box mt="16px">
                <SearchList
                  disabled={!permissionCheck([PERMISSIONS.CREATE_WAREHOUSETRANSFEREXPORT_ALL])}
                  label="Kho xuất"
                  value={stableFields?.fromWarehouse || ""}
                  onChange={(_, e) => handleChangeStableValue({ name: "fromWarehouse", value: e, searchField: "name", sortField: "order", sortOrder: "asc" })}
                  params={{ path: "/api/v1/warehouses", params: { limit: 20, skip: 0 } }}
                />
              </Box>
              {stableFields?.type === orderTrackingType.new && (
                <Box mt="16px">
                  <SearchList
                    disabled={!permissionCheck([PERMISSIONS.CREATE_WAREHOUSETRANSFEREXPORT_ALL])}
                    label="Kho nhận"
                    value={stableFields?.toWarehouse || ""}
                    onChange={(_, e) => handleChangeStableValue({ name: "toWarehouse", value: e, searchField: "name", sortField: "order", sortOrder: "asc" })}
                    params={{ path: "/api/v1/warehouses", params: { limit: 20, skip: 0, sortField: "createdAt", sortOrder: "desc", searchField: "name" } }}
                    expect={stableFields?.fromWarehouse || ""}
                  />
                  {/* <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label">Kho nhận</InputLabel>
                    <Select
                      key={stableFields?.toWarehouse?._id}
                      disabled={isScanned}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      label="Kho nhận"
                      value={stableFields?.toWarehouse?._id ?? stableFields?.toWarehouse}
                      onChange={(e) =>
                        handleChangeStableValue({
                          name: "toWarehouse",
                          value: e.target.value,
                        })
                      }
                    >
                      {Array.isArray(toWarehouse?.data) ? (
                        toWarehouse?.data
                          ?.filter((v) => v?._id !== stableFields?.fromWarehouse?._id)
                          ?.map((v) => (
                            <MenuItem
                              key={v?._id}
                              value={v?._id}
                            >
                              {v?.name}
                            </MenuItem>
                          ))
                      ) : (
                        <MenuItem
                          disabled
                          value=""
                        >
                          Không có dữ liệu
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl> */}
                </Box>
              )}
              {stableFields?.type === orderTrackingType.new && (
                <Box mt="16px">
                  <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label">Phương thức thanh toán</InputLabel>
                    <Select
                      disabled={isScanned}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      label="Phương thức thanh toán"
                      value={stableFields?.paymentMethod || ""}
                      onChange={(e) =>
                        handleChangeStableValue({
                          name: "paymentMethod",
                          value: e.target.value,
                        })
                      }
                    >
                      {Array.isArray(paymentMethods?.data) ? (
                        paymentMethods?.data?.map((v) => {
                          return (
                            <MenuItem
                              key={v?._id}
                              value={v?._id}
                            >
                              {v?.name ?? "-"}
                            </MenuItem>
                          );
                        })
                      ) : (
                        <MenuItem>Không có dữ liệu</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              )}
              <Box mt="16px">
                <SearchList
                  disabled={isScanned || (!stableFields?.paymentMethod && !item && stableFields?.type !== orderTrackingType.export)}
                  value={prodPick?.productId?.product || prodPick?.productId || ""}
                  params={{
                    path: "/api/v1/inventories",
                    params: {
                      productGroupType: stableFields?.type === orderTrackingType.export ? productGroupType.manufacture : productGroupType.retail,
                      warehouse: stableFields?.fromWarehouse?._id,
                    },
                  }}
                  onChange={(_, e) => {
                    handleChangeValue({
                      name: "productId",
                      value: e,
                    });
                  }}
                  label="Sản phẩm"
                  getOptionLabel={(option) => option?.product?.name || option?.name || ""}
                  // isOptionEqualToValue={(option, value) => console.log({ option, value })}
                />
              </Box>
              <Box mt="16px">
                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-label"> Phân loại sản phẩm</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={prodPick?.productModelId?._id || prodPick?.productModelId || ""}
                    label="Phân loại sản phẩm"
                    disabled={!prodPick?.productId?._id || (isScanned && prodPick?.mode === "update")}
                    onChange={(e) => handleChangeValue({ name: "productModelId", value: e.target.value })}
                  >
                    {Array.isArray(productDetail?.data?.productModels) ? (
                      productDetail?.data?.productModels?.map((val) => {
                        return (
                          <MenuItem
                            disabled={val?.qty - (val?.lockQty ?? 0) < 0}
                            key={val?._id}
                            value={val?._id}
                          >
                            {val?.name + ` (Tồn kho khả dụng: ${val?.qty - (val?.lockQty ?? 0)})`}
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem>Không có dữ liệu</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
              <Box mt="16px">
                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-label"> Số lô</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={prodPick?.batch?._id || prodPick?.batch || ""}
                    disabled={!prodPick?.productModelId || (isScanned && prodPick?.mode === "update")}
                    label="Số lô"
                    onChange={(e) => handleChangeValue({ name: "batch", value: e.target.value })}
                  >
                    {Array.isArray(batch?.data) && batch?.data?.length > 0 ? (
                      batch?.data?.map((v) => {
                        return (
                          <MenuItem
                            key={v?.batch?._id}
                            value={v?.batch?._id}
                            disabled={v?.batch?.status !== "Activated"}
                          >
                            {`${v?.batch?.code} (Tồn kho: ${v?.qty ?? 0} ${UNIT_LIST_CONVERT[v?.product?.unit]})`}
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem value={undefined}>Không có dữ liệu</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ mt: "16px" }}>
                <TextField
                  fullWidth
                  type="number"
                  value={prodPick?.qtyCal || ""}
                  label="Số lượng"
                  onChange={(e) => {
                    setProdPick((prev) => ({ ...prev, qtyCal: e.target.value }));
                  }}
                  disabled={!prodPick?.batch}
                  error={Number(prodPick?.qtyCal) < totalTransferDetail || isOver}
                  helperText={Number(prodPick?.qtyCal) < totalTransferDetail ? "Số lượng không được nhỏ hơn SL đã scan" : isOver ? "Số lượng vượt quá tồn kho" : null}
                  inputProps={{ min: 0 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{ pl: 1, pr: 0.5, pointerEvents: "auto" }}
                      >
                        <Select
                          key={prodPick?.unit}
                          size="small"
                          value={prodPick?.unit || ""}
                          label="Đơn vị nhập"
                          disabled={!prodPick?.unit}
                          onChange={(e) => setProdPick((prev) => ({ ...prev, unit: e.target.value }))}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                            "& .MuiSelect-select": { pr: "24px" },
                          }}
                        >
                          {UNIT_LIST.filter((v) => v?.type?.includes(prodPick?.unit))?.map((v) => {
                            return (
                              <MenuItem
                                key={v?.id}
                                value={v?.id}
                              >
                                {v?.label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box mt="16px">
                <TextField
                  type="number"
                  fullWidth
                  label="Chiết khấu (%)"
                  value={prodPick?.discount || 0}
                  InputProps={{ inputProps: { min: 0, max: 100 } }}
                  disabled={!prodPick?.batch}
                  error={prodPick?.discount > 100}
                  helperText={prodPick?.discount > 100 ? "Vượt quá mức chiết khấu cho phép" : ""}
                  onChange={(e) => handleChangeValue({ name: "discount", value: Number(e.target.value) })}
                />
              </Box>
              <Box mt="16px">
                <MallScannerMUI
                  disabled={!prodPick?.qtyCal || prodPick?.discount > 100}
                  getValue={setProdPick}
                  value={prodPick}
                  label="Scan mã sản phẩm"
                  onCreate={!prodPick?.transferDetailId ? () => handleCreateProd(WAREHOUSE_TRANSFER_CREATE_TYPE.CREATE_CODE) : undefined}
                  full={(totalTransferDetail !== 0 && totalTransferDetail === (prodPick?.qtyCal ?? 0)) || (isScanned && prodPick?.mode === "update")}
                />
                <Typography fontWeight="bold">
                  Đã scan: {totalTransferDetail ?? 0}/{prodPick?.qtyCal ?? 0}
                </Typography>
              </Box>
              <Box mt="12px">
                <TextField
                  label="Ghi chú"
                  fullWidth
                  value={prodPick?.note || ""}
                  onChange={(e) => handleChangeValue({ name: "note", value: e.target.value })}
                />
              </Box>
              {[orderTrackingType.remove]?.includes(stableFields?.type) && (
                <Box mt="16px">
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    label="Lý do "
                    value={prodPick?.note || ""}
                    onChange={(e) =>
                      handleChangeValue({
                        name: "note",
                        value: e.target.value,
                      })
                    }
                  />
                </Box>
              )}
              <Box mt="16px">
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xls,.xlsx"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <Button
                  disabled={!prodPick?.qtyCal}
                  fullWidth
                  variant="outlined"
                  startIcon={!isFileUploaded ? <UploadCloud /> : <CheckCircle color="success" />}
                  sx={{ height: "100%", display: "flex", justifyContent: "left" }}
                  onClick={handleClick}
                >
                  {!isFileUploaded ? "Tải lên danh sách số serial" : "Đã tải lên 1 file"}
                </Button>
              </Box>
               
            </Grid>
            {/* TABLE */}
            <Grid
              item
              xs={7}
            >
              <ExportProductsTable
                prodList={prodList}
                setProdPick={setProdPick}
                onRemove={confirmRemove}
                totalQty={totalQty}
                total={total}
              />
            </Grid>
          </Grid>
        </DialogContent>
      ) : (
        <ImportReviewDialog
          val={isPrint}
          transferFileType={orderStatusFormatted.Exported}
        />
      )}
      {!isPrint && (
        <DialogActions
          display="flex"
          sx={{ p: "12px 24px 24px" }}
        >
          <Grid
            container
            spacing={2}
          >
            <Grid
              xs={5}
              item
            >
              <Button
                disabled={!prodPick?.qtyCal || prodPick?.qtyCal < totalTransferDetail || isOver || prodPick?.discount > 100}
                onClick={() => {
                  if ((!prodPick?.transferDetailId && !prodList?.length) || (!prodPick?.transferDetailId && prodPick?.qtyCal)) {
                    handleCreateProd(WAREHOUSE_TRANSFER_CREATE_TYPE.CREATE);
                    setProdPick();
                  }
                  if (prodPick?.transferDetailId && prodList?.length) {
                    setProdPick();
                  }
                  if (prodPick?.mode === "update") {
                    handleUpdateProd();
                  }
                }}
                fullWidth
                sx={{ background: "var(--confirm)", color: "var(--confirm-txt)" }}
              >
                {prodPick?.mode === "update" ? "Cập nhật" : "Thêm sản phẩm"}
              </Button>
            </Grid>
            <Grid
              xs={7}
              item
            >
              <Box sx={{ display: "flex", gap: "12px" }}>
                {updateItem?.statusOut === orderStatusFormatted.Pending ? (
                  <Button
                    onClick={handleConfirmUpdate}
                    sx={{ whiteSpace: "nowrap" }}
                    variant="outlined"
                    fullWidth
                  >
                    Xác nhận chỉnh sửa
                  </Button>
                ) : (
                  <Button
                    disabled={isFulled || !prodList?.length}
                    sx={{ whiteSpace: "nowrap" }}
                    variant="outlined"
                    fullWidth
                    onClick={() => handleExport(orderStatusFormatted.Pending)}
                  >
                    Chờ xuất hàng
                  </Button>
                )}

                <Button
                  disabled={hasUnexportedQty || !prodList?.length}
                  form="export-form"
                  type="submit"
                  sx={{ whiteSpace: "nowrap" }}
                  variant="contained"
                  fullWidth
                  onClick={() => handleExport(orderStatusFormatted.Exported)}
                >
                  Xuất hàng
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogActions>
      )}
    </>
  );
}
